import { Tag } from '@ekhodealer/ekho-common/rev';

import { formatNumberWithCommas } from './currency';

/**
 * Display status enum for order report items
 */
export enum OrderDisplayStatus {
	PAID = 'paid',
	IN_PROGRESS = 'in_progress',
	NOT_PAID = 'not_paid',
	CANCELLED = 'cancelled',
	ON_HOLD = 'on_hold',
}

/**
 * Type for display status values
 */
export type OrderDisplayStatusType = `${OrderDisplayStatus}`;

/**
 * Vehicle payment status enum from backend
 */
export enum VehiclePaymentStatus {
	FULLY_PAID = 'FULLY_PAID',
	DOWNPAYMENT_PAID = 'DOWNPAYMENT_PAID',
	NO_PAYMENTS = 'NO_PAYMENTS',
	RESERVATION_PAID = 'RESERVATION_PAID',
	RESERVATION_INITIATED = 'RESERVATION_INITIATED',
	FINANCING_GUARANTEED = 'FINANCING_GUARANTEED',
	LOST_DISPUTE = 'LOST_DISPUTE',
}

/**
 * Type for vehicle payment status values
 */
export type VehiclePaymentStatusType = `${VehiclePaymentStatus}`;

/**
 * Map vehicle payment status to display status
 */
export const VEHICLE_PAYMENT_STATUS_TO_DISPLAY_STATUS: Record<string, OrderDisplayStatusType> = {
	[VehiclePaymentStatus.FULLY_PAID]: OrderDisplayStatus.PAID,
	[VehiclePaymentStatus.DOWNPAYMENT_PAID]: OrderDisplayStatus.IN_PROGRESS,
	[VehiclePaymentStatus.NO_PAYMENTS]: OrderDisplayStatus.NOT_PAID,
	[VehiclePaymentStatus.RESERVATION_PAID]: OrderDisplayStatus.IN_PROGRESS,
	[VehiclePaymentStatus.RESERVATION_INITIATED]: OrderDisplayStatus.IN_PROGRESS,
	[VehiclePaymentStatus.FINANCING_GUARANTEED]: OrderDisplayStatus.IN_PROGRESS,
	[VehiclePaymentStatus.LOST_DISPUTE]: OrderDisplayStatus.CANCELLED,
	paid: OrderDisplayStatus.PAID,
	in_progress: OrderDisplayStatus.IN_PROGRESS,
	not_paid: OrderDisplayStatus.NOT_PAID,
	cancelled: OrderDisplayStatus.CANCELLED,
	on_hold: OrderDisplayStatus.ON_HOLD,
};

/**
 * Map vehicle payment status to display strings
 */
export const VEHICLE_PAYMENT_STATUS_DISPLAY_MAP: Record<VehiclePaymentStatusType, string> = {
	[VehiclePaymentStatus.FULLY_PAID]: 'Fully paid',
	[VehiclePaymentStatus.DOWNPAYMENT_PAID]: 'Downpayment paid',
	[VehiclePaymentStatus.NO_PAYMENTS]: 'No payments',
	[VehiclePaymentStatus.RESERVATION_PAID]: 'Reservation paid',
	[VehiclePaymentStatus.RESERVATION_INITIATED]: 'Reservation initiated',
	[VehiclePaymentStatus.FINANCING_GUARANTEED]: 'Financing guaranteed',
	[VehiclePaymentStatus.LOST_DISPUTE]: 'Lost dispute',
};

/**
 * Formats a number value from backend (can be string or number)
 * Returns formatted string with commas or '--' if null/undefined
 */
export function formatReportNumber(value: number | string | undefined | null): string {
	if (value === null || value === undefined) return '--';
	if (typeof value === 'string') return value;
	return formatNumberWithCommas(value);
}

/**
 * Formats a date from timestamp (number or string)
 * Returns formatted date string or '--' if invalid
 */
export function formatReportDate(value: number | string | undefined | null): string {
	if (!value) return '--';
	const timestamp = typeof value === 'string' ? parseInt(value, 10) : value;
	if (isNaN(timestamp)) return '--';
	const date = new Date(timestamp);
	return date.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	});
}

/**
 * Formats VehiclePaymentStatus enum values for display
 * Uses the display map from ordersAtoms, falls back to snake_case conversion
 */
export function formatVehiclePaymentStatus(status: string | undefined | null): string {
	if (!status) return '--';

	// Check if status exists in display map
	if (status in VEHICLE_PAYMENT_STATUS_DISPLAY_MAP) {
		return VEHICLE_PAYMENT_STATUS_DISPLAY_MAP[status as VehiclePaymentStatusType];
	}

	// Fallback: convert enum format to readable string
	return status
		.split('_')
		.map((word) => word.charAt(0) + word.slice(1).toLowerCase())
		.join(' ');
}

/**
 * Returns a Tag component with appropriate accent color based on vehicle payment status
 */
export function getVehiclePaymentStatusTag(status: string | undefined | null) {
	if (!status) {
		return (
			<Tag appearance='outline' accent={'neutral'}>
				--
			</Tag>
		);
	}

	const formattedStatus = formatVehiclePaymentStatus(status);

	switch (status) {
		case VehiclePaymentStatus.FULLY_PAID:
			return (
				<Tag appearance='outline' accent={3}>
					{formattedStatus}
				</Tag>
			);
		case VehiclePaymentStatus.DOWNPAYMENT_PAID:
		case VehiclePaymentStatus.RESERVATION_PAID:
		case VehiclePaymentStatus.RESERVATION_INITIATED:
		case VehiclePaymentStatus.FINANCING_GUARANTEED:
			return (
				<Tag appearance='outline' accent={4}>
					{formattedStatus}
				</Tag>
			);
		case VehiclePaymentStatus.NO_PAYMENTS:
		case VehiclePaymentStatus.LOST_DISPUTE:
			return (
				<Tag appearance='outline' accent={1}>
					{formattedStatus}
				</Tag>
			);
		default:
			return (
				<Tag appearance='outline' accent={'neutral'}>
					{formattedStatus}
				</Tag>
			);
	}
}

/**
 * Returns a Tag component with appropriate accent color based on payment/transfer status
 * Used for payment reconciliation statuses (receipt status, transfer status)
 */
export function getPaymentStatusTag(status: string | undefined | null) {
	const normalizedStatus = status?.toLowerCase() || '';
	switch (normalizedStatus) {
		case 'completed':
		case 'sent':
		case 'succeeded':
			return (
				<Tag appearance='outline' accent={3}>
					{status || 'Unknown'}
				</Tag>
			);
		case 'pending':
		case 'processing':
			return (
				<Tag appearance='outline' accent={4}>
					{status || 'Unknown'}
				</Tag>
			);
		case 'failed':
		case 'error':
			return (
				<Tag appearance='outline' accent={1}>
					{status || 'Unknown'}
				</Tag>
			);
		case 'cancelled':
		case 'canceled':
			return (
				<Tag appearance='outline' accent={'neutral'}>
					{status || 'Unknown'}
				</Tag>
			);
		default:
			return (
				<Tag appearance='outline' accent={'neutral'}>
					{status || 'Unknown'}
				</Tag>
			);
	}
}
